import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { REMOVE_TOTP } from "src/gql/mutations/totp";
import { Spacing } from "src/types/enum";
import { useListTotpQuery } from "src/types/graphql";

interface TotpDevice {
  id: string;
  deviceName?: string | null;
  createdAt: string;
}

export default function AccountSettingsPage() {
  const { data, refetch } = useListTotpQuery({ fetchPolicy: "cache-and-network" });
  const totps: TotpDevice[] = (data?.listTotp ?? []) as TotpDevice[];
  const [removeTotpMutation, { loading: removing, error: removeError }] = useMutation(REMOVE_TOTP);
  const [removingDevice, setRemovingDevice] = useState<TotpDevice | null>(null);
  const [password, setPassword] = useState("");

  const onRemove = async () => {
    if (!removingDevice) {
      return;
    }
    const result = await removeTotpMutation({
      variables: { totpId: removingDevice.id, currentPassword: password },
    });
    if (result.data?.removeTotp?.status === "SUCCESS") {
      await refetch();
      setRemovingDevice(null);
      setPassword("");
    }
  };

  return (
    <>
      <Box mb={Spacing.m} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h3" color="textPrimary">
          Account settings
        </Typography>
      </Box>

      <Box mb={Spacing.m} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" color="textPrimary">
          Two factor authentication devices
        </Typography>
      </Box>

      {totps.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Device name</TableCell>
              <TableCell>Added at</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {totps.map((totp) => (
              <TableRow key={totp.id}>
                <TableCell>{totp.deviceName}</TableCell>
                <TableCell>
                  <time dateTime={totp.createdAt}>{new Date(totp.createdAt).toLocaleString()}</time>
                </TableCell>
                <TableCell>
                  <Button onClick={() => setRemovingDevice(totp)}>Remove</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box m={Spacing.m} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body1" color="textPrimary">
            No devices configured yet
          </Typography>
        </Box>
      )}

      <Box m={Spacing.m} display="flex" alignItems="center" justifyContent="space-between">
        <Link to="/account/new-device">
          <Button variant="contained">Configure new device</Button>
        </Link>
      </Box>

      <Dialog open={removingDevice !== null} onClose={() => setRemovingDevice(null)}>
        <DialogTitle>Remove this device?</DialogTitle>
        <DialogContent>
          <Typography mb={2} variant="body1">
            Removing &quot;{removingDevice?.deviceName}&quot; weakens your two-factor protection.
            Enter your password to confirm.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {removeError ? (
            <Typography color="error" mt={1} variant="body2">
              {removeError.message}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingDevice(null)}>Cancel</Button>
          <Button
            color="warning"
            disabled={removing || !password}
            onClick={onRemove}
            variant="contained"
          >
            Remove device
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
